import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FaceRetouchingNatural as FaceIcon } from '@mui/icons-material';
import Stack from '@mui/material/Stack';
import { Link, useNavigate } from 'react-router';
import ThemeSwitcher from './ThemeSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationsMenu from './NotificationsMenu';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from './CustomNotification';
import FaceCaptureDialog from '../../components/FaceCaptureDialog';
import { faceAuthService } from '../../services/faceAuthService';

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: 'solid',
  borderColor: (theme.vars ?? theme).palette.divider,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  backdropFilter: 'blur(12px)',
  background: 'rgba(255, 255, 255, 0.95)',
  zIndex: theme.zIndex.drawer + 1,
  transition: 'all 0.3s ease-in-out',
  ...theme.applyStyles('dark', {
    borderColor: 'rgba(255, 255, 255, 0.12)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.3)',
    background: 'linear-gradient(180deg, rgba(30, 35, 45, 0.98) 0%, rgba(25, 30, 38, 0.98) 100%)',
  }),
}));

const LogoContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  height: 56,
  display: 'flex',
  alignItems: 'center',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  '& img': {
    maxHeight: 56,
  },
  '& svg': {
    height: 56,
    width: 'auto',
  },
}));

export interface DashboardHeaderProps {
  logo?: React.ReactNode;
  title?: string;
  menuOpen: boolean;
  onToggleMenu: (open: boolean) => void;
}

export default function DashboardHeader({
  logo,
  title,
  menuOpen,
  onToggleMenu,
}: DashboardHeaderProps) {
  const theme = useTheme();
  const { t, direction } = useLanguage();
  const { logout, faceAuthStatus, refreshFaceAuthStatus } = useAuth();
  const navigate = useNavigate();
  const { show } = useNotification();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFaceEnrollDialog, setShowFaceEnrollDialog] = React.useState(false);
  const [isEnrolling, setIsEnrolling] = React.useState(false);
  const [checkedIn, setCheckedIn] = React.useState<boolean>(() => {
    const stored = localStorage.getItem('face_check_in_date');
    return stored === new Date().toDateString();
  });
  const optionsMenuOpen = Boolean(anchorEl);

  // Check if face is already registered
  const isFaceRegistered = faceAuthStatus?.has_face_registered ?? false;

  const handleMenuOpen = React.useCallback(() => {
    onToggleMenu(!menuOpen);
  }, [menuOpen, onToggleMenu]);

  const handleOptionsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOptionsMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = React.useCallback(async () => {
    handleOptionsMenuClose();
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleFaceEnrollClick = () => {

    if (!isFaceRegistered) {
      return;
    }

    handleOptionsMenuClose();
    // Delay opening dialog to ensure menu is closed
    setTimeout(() => {
      setShowFaceEnrollDialog(true);
    }, 100);
  };

  const getLocation = async (): Promise<{ latitude?: number; longitude?: number }> => {
    if (!('geolocation' in navigator)) {
      console.warn('[DashboardHeader] Geolocation not supported by browser');
      return {};
    }
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });
      return { latitude: position.coords.latitude, longitude: position.coords.longitude };
    } catch (locationError: any) {
      console.warn('[DashboardHeader] Failed to get location:', locationError.message);
      return {};
    }
  };

  const handleFaceEnrollCapture = async (imageBase64: string) => {
    setIsEnrolling(true);

    try {
      const { latitude, longitude } = await getLocation();

      const response = checkedIn
        ? await faceAuthService.checkOutWithFace(imageBase64, latitude, longitude)
        : await faceAuthService.checkInWithFace(imageBase64, latitude, longitude);

      if (response.success) {
        setShowFaceEnrollDialog(false);
        await refreshFaceAuthStatus();
        if (checkedIn) {
          localStorage.removeItem('face_check_in_date');
          setCheckedIn(false);
          show(t('faceAuth.checkOutSuccess') || 'Checked out successfully', { severity: 'success' });
        } else {
          localStorage.setItem('face_check_in_date', new Date().toDateString());
          setCheckedIn(true);
          show(t('faceAuth.checkInSuccess') || 'Checked in successfully', { severity: 'success' });
        }
      } else {
        const action = checkedIn ? 'check-out' : 'check-in';
        const errorMessage = response.message || (checkedIn
          ? (t('faceAuth.checkOutFailed') || 'Check-out failed')
          : (t('faceAuth.checkInFailed') || 'Check-in failed'));
        console.error(`[DashboardHeader] Face ${action} failed:`, errorMessage);
        show(errorMessage, { severity: 'error' });
      }
    } catch (err: any) {
      const errorMessage = err.message || (checkedIn
        ? (t('faceAuth.checkOutError') || 'An error occurred during check-out')
        : (t('faceAuth.checkInError') || 'An error occurred during check-in'));
      show(errorMessage, { severity: 'error' });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleFaceEnrollDialogClose = () => {
    setShowFaceEnrollDialog(false);
    setIsEnrolling(false);
  };

  const getMenuIcon = React.useCallback(
    (isExpanded: boolean) => {
      const expandMenuActionText = t('header.expand');
      const collapseMenuActionText = t('header.collapse');

      return (
        <Tooltip
          title={`${isExpanded ? collapseMenuActionText : expandMenuActionText} ${t('header.menu')}`}
          enterDelay={1000}
        >
          <div>
            <IconButton
              size="medium"
              aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText} ${t('header.navigationMenu')}`}
              onClick={handleMenuOpen}
              sx={(theme) => ({
                color: theme.palette.text.primary,
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                borderRadius: '12px',
                border: '1px solid transparent',
                transition: 'all 0.3s ease',
                ...theme.applyStyles('dark', {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                }),
                '&:hover': {
                  backgroundColor: '#028bcb',
                  color: '#fff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(2, 139, 203, 0.3)',
                  border: '1px solid rgba(2, 139, 203, 0.5)',
                  ...theme.applyStyles('dark', {
                    boxShadow: '0 6px 20px rgba(2, 139, 203, 0.4), 0 0 0 1px rgba(2, 139, 203, 0.5)',
                  }),
                },
              })}
            >
              {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    [handleMenuOpen, t],
  );

  return (
    <AppBar color="inherit" position="absolute" sx={{ displayPrint: 'none' }}>
      <Toolbar sx={{
        backgroundColor: 'inherit',
        mx: { xs: -0.75, sm: -1 },
        minHeight: { xs: 64, sm: 70 },
        px: { xs: 2, sm: 3 },
      }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <Stack direction="row" alignItems="center">
            <Box sx={{
              mr: direction === 'rtl' ? 0 : 1,
              ml: direction === 'rtl' ? 1 : 0
            }}>
              {getMenuIcon(menuOpen)}
            </Box>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Stack direction="row" alignItems="center">
                {logo ? <LogoContainer>{logo}</LogoContainer> : null}
                {title ? (
                  <Typography
                    variant="h6"
                    sx={{
                      color: (theme.vars ?? theme).palette.primary.main,
                      fontWeight: '700',
                      ml: direction === 'rtl' ? 0 : 1,
                      mr: direction === 'rtl' ? 1 : 0,
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}
                  >
                    {title}
                  </Typography>
                ) : null}
              </Stack>
            </Link>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              marginLeft: direction === 'rtl' ? 0 : 'auto',
              marginRight: direction === 'rtl' ? 'auto' : 0
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <NotificationsMenu />
              <LanguageSwitcher />
              <ThemeSwitcher />
              <Tooltip title={t('header.options') || 'Options'} enterDelay={1000}>
                <IconButton
                  size="medium"
                  onClick={handleOptionsMenuClick}
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    borderRadius: '12px',
                    border: '1px solid transparent',
                    transition: 'all 0.3s ease',
                    ...theme.applyStyles('dark', {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                    }),
                    '&:hover': {
                      backgroundColor: '#028bcb',
                      color: '#fff',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(2, 139, 203, 0.3)',
                      border: '1px solid rgba(2, 139, 203, 0.5)',
                      ...theme.applyStyles('dark', {
                        boxShadow: '0 6px 20px rgba(2, 139, 203, 0.4), 0 0 0 1px rgba(2, 139, 203, 0.5)',
                      }),
                    },
                  })}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={optionsMenuOpen}
                onClose={handleOptionsMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: direction === 'rtl' ? 'left' : 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: direction === 'rtl' ? 'left' : 'right',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <MenuItem
                  onClick={handleFaceEnrollClick}
                  disabled={isEnrolling || !isFaceRegistered}
                >
                  <ListItemIcon>
                    {isEnrolling ? (
                      <CircularProgress size={20} />
                    ) : (
                      <FaceIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText>
                    {!isFaceRegistered
                      ? (t('faceAuth.notRegistered') || 'Face ID Not Registered')
                      : checkedIn
                        ? (t('faceAuth.checkOut') || 'Face Check-Out')
                        : (t('faceAuth.checkIn') || 'Face Check-In')
                    }
                  </ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('auth.logout')}</ListItemText>
                </MenuItem>
              </Menu>
            </Stack>
          </Stack>
        </Stack>
      </Toolbar>

      {/* Face Check-in Dialog - Outside Menu to prevent unmounting issues */}
      <FaceCaptureDialog
        open={showFaceEnrollDialog}
        onClose={handleFaceEnrollDialogClose}
        onCapture={handleFaceEnrollCapture}
        title={checkedIn
          ? (t('faceAuth.checkOutTitle') || 'Face Check-Out')
          : (t('faceAuth.checkInTitle') || 'Face Check-In')}
        description={checkedIn
          ? (t('faceAuth.checkOutDescription') || 'Position your face within the circle to check out.')
          : (t('faceAuth.checkInDescription') || 'Position your face within the circle to check in.')}
      />
    </AppBar>
  );
}
